// Story 11.4 — Fila de un log dentro del LogPanel.

import { ChevronDown, ChevronRight } from 'lucide-react';
import { memo, useState } from 'react';
import { type LogEntry, type LogLevel, shortTime } from '@/lib/event-to-log';
import { cn } from '@/lib/utils';

interface Props {
  entry: LogEntry;
  searchTerm?: string;
}

const LEVEL_BADGE: Record<LogLevel, string> = {
  debug: 'bg-muted text-muted-foreground',
  info: 'bg-info/15 text-info',
  warn: 'bg-warning/15 text-warning',
  error: 'bg-destructive/15 text-destructive',
};

function LogEntryImpl({ entry, searchTerm = '' }: Props): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const hasContext = Object.keys(entry.context).length > 0;

  return (
    <li
      className={cn(
        'border-b border-border/50 px-3 py-1.5 font-mono text-xs',
        entry.fromClient && 'bg-muted/30',
      )}
      data-level={entry.level}
      data-event={entry.eventType}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          onClick={() => hasContext && setExpanded((v) => !v)}
          className={cn('shrink-0 text-muted-foreground', !hasContext && 'invisible')}
          aria-label={expanded ? 'Colapsar contexto' : 'Expandir contexto'}
        >
          {expanded ? (
            <ChevronDown className="h-3 w-3" aria-hidden />
          ) : (
            <ChevronRight className="h-3 w-3" aria-hidden />
          )}
        </button>
        <span className="shrink-0 text-muted-foreground">{shortTime(entry.timestamp)}</span>
        <span
          className={cn(
            'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase',
            LEVEL_BADGE[entry.level],
          )}
        >
          {entry.fromClient ? `${entry.level}·client` : entry.level}
        </span>
        <span className="flex-1 break-words">{highlight(entry.message, searchTerm)}</span>
      </div>
      {expanded && hasContext && (
        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap rounded bg-muted/40 p-2 text-[10px] text-muted-foreground">
          {safeStringify(entry.context)}
        </pre>
      )}
    </li>
  );
}

export const LogEntryRow = memo(LogEntryImpl);

function highlight(message: string, needle: string): React.ReactNode {
  // P-441 (review 11.4): highlight TODAS las ocurrencias, no solo la
  // primera. Antes "user X user Y" con search "user" solo pintaba la
  // primera. Iteramos hasta agotar matches; React renderiza vía JSX (sin
  // dangerouslySetInnerHTML) → no XSS.
  const trimmed = needle.trim();
  if (!trimmed) return message;
  const lowerMsg = message.toLowerCase();
  const lowerNeedle = trimmed.toLowerCase();
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  while (cursor < message.length) {
    const idx = lowerMsg.indexOf(lowerNeedle, cursor);
    if (idx < 0) {
      parts.push(message.slice(cursor));
      break;
    }
    if (idx > cursor) parts.push(message.slice(cursor, idx));
    parts.push(
      <mark key={idx} className="rounded bg-warning/30 px-0.5">
        {message.slice(idx, idx + trimmed.length)}
      </mark>,
    );
    cursor = idx + trimmed.length;
  }
  return <>{parts}</>;
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '[unserializable]';
  }
}
